import { defineComponent } from "@openuidev/lang-core";
import { z } from "zod";

// Humanities widgets. See ../widgets.ts for the rules all widgets follow.

export const Timeline = defineComponent({
  name: "Timeline",
  description:
    'A short timeline of 2 to 10 events in order, each with a date label (a string such as "1789" or "IIIe siècle av. J.-C."), a title and an optional detail the child can open. Use for history and for the order of events in a story. `focus` optionally highlights one event by its 0-based index.',
  props: z.object({
    events: z
      .array(
        z.object({
          when: z.string().min(1).max(40),
          title: z.string().min(1).max(80),
          detail: z.string().min(1).max(300).optional(),
        }),
      )
      .min(2)
      .max(10),
    focus: z.number().int().min(0).max(9).optional(),
  }),
  component: null,
});

export const SourceCard = defineComponent({
  name: "SourceCard",
  description:
    "A primary source excerpt (a text, speech, letter, law, poem, or a description of an image) with its attribution and 1 to 3 guiding questions the child can ask you about. Use in history, civics and literature to read a source closely. Quote the source faithfully; never put the answer to a question in the card.",
  props: z.object({
    excerpt: z.string().min(1).max(600),
    attribution: z.object({
      author: z.string().min(1).max(80).optional(),
      work: z.string().min(1).max(120).optional(),
      date: z.string().min(1).max(40).optional(),
    }),
    kind: z.enum(["text", "speech", "letter", "law", "poem", "image-description"]),
    questions: z.array(z.string().min(1).max(200)).min(1).max(3),
  }),
  component: null,
});

export const WordExplorer = defineComponent({
  name: "WordExplorer",
  description:
    "A vocabulary card for one word: its meaning and an example sentence, with an optional pronunciation hint in plain letters, origin (etymology) and up to 6 related words the child can open. Use when a new or tricky word comes up in any subject. Write null to skip an optional field before one you fill.",
  props: z.object({
    word: z.string().min(1).max(40),
    meaning: z.string().min(1).max(200),
    example: z.string().min(1).max(200),
    pronunciation: z.string().min(1).max(60).nullish(),
    origin: z.string().min(1).max(160).nullish(),
    related: z.array(z.string().min(1).max(40)).min(1).max(6).nullish(),
  }),
  component: null,
});

export const humanitiesWidgets = [Timeline, SourceCard, WordExplorer] as const;
